import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/ui/Button";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Fallback UI extracted into a function component so it can call the
 * `useTranslation` hook — the outer `ErrorBoundary` MUST stay a class
 * component (React error boundaries require `getDerivedStateFromError` /
 * `componentDidCatch`, which have no hook-based equivalent).
 */
function ErrorBoundaryFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="text-lg font-semibold text-gray-900">
        {t("components.errorBoundary.title")}
      </h1>
      <p className="max-w-md text-sm text-gray-600">
        {t("components.errorBoundary.description")}
      </p>
      <Button onClick={() => window.location.reload()}>
        {t("components.errorBoundary.reloadButton")}
      </Button>
    </div>
  );
}

/**
 * Top-level error boundary — converts an unhandled render/runtime throw into a
 * recoverable error card instead of a blank white screen. Wraps the whole app
 * tree (App.tsx).
 *
 * Defense-in-depth: individual surfaces still guard their own API data (a
 * malformed 200 body must not reach a `.map`/`.find` on `undefined`), but this
 * boundary guarantees that ANY render-time throw — a future regression, an
 * un-anticipated partial response — degrades gracefully rather than
 * white-screening the SPA. React error boundaries catch render/lifecycle throws
 * only (not event-handler or async errors), which is exactly the
 * render-on-bad-data class this hardens against.
 */
export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Diagnostics only — the production observability path (global error
    // handlers) captures unhandled errors; this keeps a component-stack
    // breadcrumb in the console for triage.
    console.error("Unhandled render error:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorBoundaryFallback />;
    }
    return this.props.children;
  }
}
